import * as Actions from '../actions/optionActions';
import Option from '../../entities/option';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type OptionAction = { 
    type: string; 
    option: Option | null; 
    optionId: string | Option; 
    partial: boolean; 
    lastFetch: number; 
    options: Option[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Option> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Option[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Option> | null, firmId: string, userId: string, updates?: Option[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'option', 'options', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Option>} data
 * @param {Option[]} [updates]
 * @returns {ArrayMap<Option>}
 */
function updateStorage(data: ArrayMap<Option>, updates?: Option[]): ArrayMap<Option> {
	if (logging) console.debug('⮂ optionReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Option[]} [updates]
 * @returns {ArrayMap<Option>}
 */
function updateStorageNoReturn(updates?: Option[]): void {
	if (logging) console.debug('⮂ optionReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Option[]} [data]
 * @returns {ArrayMap<Option>}
 */
function createState(data?: Option[] | ArrayMap<Option>): ArrayMap<Option> {
	if (!data) return ArrayMapRegistry.clone<Option>('option', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Option>('option', data.map(e => e));
    else return ArrayMapRegistry.clone<Option>('option', data);
}

function options(state: ArrayMap<Option>, action: OptionAction): ArrayMap<Option> {
	if (!state) state = createState();

	let st: ArrayMap<Option>;

	switch (action.type) {
		// action.option - the new value of option with id action.id
		// action.optionId - id of the target, used if new value is null
		case Actions.OPTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ optionReducer: FETCH_ONE_SUCCESS', action);

			if (action.option === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.optionId)) {
				st = state.clone();
				st.set(action.option);
				// state.clear();

				updateStorageNoReturn([action.option]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.options
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.options
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.options
			 !partial, !stateEmpty, fetchEmpty	-> D return options in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge options in state newer than those in action.options
		 */
		case Actions.OPTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.options.length === 0;
			const fetched = action.options.map(option => new Option({...option, persisted: true}));

			if (logging) console.debug(`⮂ optionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} options with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ optionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ optionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Option> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Option[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(option => {
					const current = st.get(option.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && option.status === 'D') {
							// Remove it from state
							toUpdate.push(option);
							st.delete(option);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (option.addedOn > current.addedOn) {
								toUpdate.push(option);
								st.set(option);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (option.status !== 'D') {
							toUpdate.push(option);
							st.set(option);
						}
						else if (logging) console.debug('⮂ optionReducer: partialMerge: skipping deleted option not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ optionReducer: empty: options', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ optionReducer: fullMerge: options', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localOption => {
                    // If this option has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localOption.persisted) {
                        const fetchedOption = st.get(localOption.id);
                        if (!fetchedOption || localOption.addedOn > fetchedOption.addedOn) st.set(localOption);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.OPTION_FETCH_ONE_FAILURE:
			console.error('OPTION_FETCH_ONE_FAILURE', action.optionId, action.err);

			// No update to storage required
			return state;

		case Actions.OPTION_FETCH_FAILURE:
			console.error('OPTION_FETCH_FAILURE', action.optionId, action.err);

			// No update to storage required
			return state;

		case Actions.OPTION_RESTORE: {
		    let option = new Option({...action.option});
            st = state.clone();

            const existing = st.get(option.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (option.addedOn >= existing.addedOn) {
                    st.set(option);
                    return updateStorage(st, [option]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (option.status === 'D') return state;

                st.set(option);
                return updateStorage(st, [option]);
            }
        }
            
		case Actions.OPTION_SAVE: {
		    // console.debug('⮂ OPTION_SAVE reducer', action.option?.id);
		    
			// clone the option
			let option = new Option({...action.option});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new option in the state
			// If this is a create, add to the queue
			const _option = st.get(option.id);

			if (!_option) st.set(option);
			else {
				if (option.addedOn >= _option.addedOn) st.set(option);
			}

			return updateStorage(st, [option]);
		}

		case Actions.OPTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ OPTION_SAVE_MANY reducer', action.options.map(option => option.id).join(','));
		    
			// clone the options
			let toProcess = action.options.map(option => new Option(option));

			st = state.clone();
			// state.clear();

			const updates: Option[] = [];

			toProcess.forEach(option => {
				// Add or update the new option in the state
				// If this is a create, add to the queue
				const existing = st.get(option.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (option.status !== 'D') {
						st.push(option);
						updates.push(option);
					}
					else if (logging) console.debug('⮂ OPTION_SAVE_MANY: skipping deleted option not in state');
				}
				else {
					if (option.addedOn >= existing.addedOn) {
						st.set(option);
						updates.push(option);
					}
					else if (logging) console.debug('⮂ Skipping, existing option had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.OPTION_REMOVE: {
			// clone the option
			const option = new Option({...action.option});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(option);

			return updateStorage(st, [option]);
		}

		case Actions.OPTION_RESTORE_FAILURE:
		case Actions.OPTION_SAVE_FAILURE: {
		    console.warn('OPTION_SAVE_FAILURE reducer', action.option?.id);
		    if (!action.option?.id) {
				console.warn('OPTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const option = ensure(Option, action.option);
			if (!option) {
				console.error('Could not find option after call to save');
				return state;
			}

			st = state.clone();

			const _optionRef = state.get(option.id);

			if (_optionRef && (_optionRef.addedOn === option.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _option = new Option({..._optionRef});
				_option.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_option.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('option', _option).then(r => {
						if (r.isErr()) console.error(`⮂ optionReducer: failed to save option with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ optionReducer: saved option with fatalError info');
					}).catch(err => console.error(`⮂ optionReducer: failed to save option with fatalError info: ${err.message}`));

				st.set(_option);
			}

			return updateStorage(st);
		}

		case Actions.OPTION_SAVE_MANY_FAILURE: {
		    console.warn('OPTION_SAVE_MANY_FAILURE reducer', action.options.map(option => option.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.OPTION_RESTORE_SUCCESS:
		case Actions.OPTION_SAVE_SUCCESS: {
			if (!action.option?.id) {
				console.warn('OPTION_SAVE_SUCCESS reducer');
                return state;
			}

			const optionRef = state.get(action.option?.id);
			if (!optionRef) {
				console.warn('Could not find option after call to save');
                return state;
			}

			st = state.clone();

			if (action.option.addedOn >= optionRef.addedOn) {
				st.set(action.option);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const option = new Option({...optionRef, persisted: true});
				st.set(option);
			}

			return updateStorage(st, [action.option]);
		}

		case Actions.OPTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ OPTION_SAVE_MANY_SUCCESS reducer', action.options.map(option => option.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const options: Option[] = [];

			action.options.forEach(e => {
				const optionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!optionRef) console.warn('Could not find option after call to save');
				else if (e.addedOn >= optionRef.addedOn) {
				    st.set(e);
				    options.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const option = new Option({...optionRef, persisted: true});
					st.set(option);
					options.push(option);
				}
			});

			return updateStorage(st, options);
		}

		case Actions.OPTION_REMOVE_FAILURE: {
		    console.warn('OPTION_REMOVE_FAILURE action', action.option?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.OPTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.OPTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ OPTION_RESTORE_MANY reducer', action.options.map(option => option.id).join(','));

			const toProcess = action.options.map(option => new Option({...option}));
			st = state.clone();

			const updates: Option[] = [];

			toProcess.forEach(option => {
				const existing = st.get(option.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (option.addedOn >= existing.addedOn) {
						st.set(option);
						updates.push(option);
					}
					else if (logging) console.debug('⮂ OPTION_RESTORE_MANY: skipping older version of option');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (option.status !== 'D') {
						st.set(option);
						updates.push(option);
					}
					else if (logging) console.debug('⮂ OPTION_RESTORE_MANY: skipping deleted option not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.OPTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ OPTION_REMOVE_MANY reducer', action.options.map(option => option.id).join(','));

			const toProcess = action.options.map(option => new Option({...option}));
			st = state.clone();

			toProcess.forEach(option => {
				st.delete(option);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default options;